// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getPageLinkByNodeId, LinkModel, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import * as React from "react";
import { css } from "styled-components";

/* Starter Custom Code - Start */
import { Dictionary } from "@insite/client-framework/Common/Types";
import { IconPresentationProps } from "@insite/mobius/Icon";
import PhoneLine, { PhoneLineOffset } from "@store/Library/Components/Toolkit/Icons/Phone/PhoneLine";
import PinLine, { PinLineOffset } from "@store/Library/Components/Toolkit/Icons/Pin/PinLine";
import { linkStyles } from "@store/Themes/common/mixins/link";
import { menuSecondaryNavigationItemStyles } from "@store/Themes/common/mixins/menu";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { LinkModelWithIcon } from "@toolkit/Framework/Store/Links/LinksSelectors";
import { IconOffset } from "@toolkit/Framework/Types/Icon";
import { iconScalableStyles } from "@toolkit/Themes/common/mixins";

// Sas --->
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import breakpointMediaQueries, { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import useIsScrollTop from "@store/Framework/Common/Hooks/useIsScrollTop";
import { widgetWrapperStyles } from "@store/Library/Components/Toolkit/WidgetWrapper";
import { contentSpacingStyles } from "@store/Themes/common/mixins/spacing";
// <--- Sas

export interface HeaderLinkListIcon {
    displayName: string;
    src: IconPresentationProps["src"];
    size?: string;
    offset?: IconOffset;
}

export const headerLinkListIcons: Dictionary<HeaderLinkListIcon> = {
    location: {
        displayName: "Location",
        size: pxToRem(22),
        src: PinLine,
        offset: PinLineOffset,
    },
    phone: {
        displayName: "Phone",
        size: pxToRem(20),
        src: PhoneLine,
        offset: PhoneLineOffset,
    },
};
/* Starter Custom Code - End */

const enum fields {
    links = "links",
    customCSS = "customCSS",
    // Sas
    direction = "direction",
    alignment = "alignment",
}

// Sas --->
interface CustomLinkModel extends LinkModel {
    isButton?: boolean;
    buttonVariant: "headerType" | "primary" | "secondary" | "tertiary";
}
// <--- Sas

interface OwnProps extends WidgetProps {
    fields: {
        [fields.links]: CustomLinkModel[]; // Sas
        [fields.customCSS]: string;
        // Sas
        [fields.direction]: "vertical" | "horizontal";
        [fields.alignment]: "left" | "center" | "right";
    };
    extendedStyles?: HeaderLinkListStyles;
}

export interface HeaderLinkListStyles {
    headerLinkListWrapper?: InjectableCss;
    link?: LinkPresentationProps;
    // Starter Custom Code
    linkWrapper?: InjectableCss;
    // Sas
    button?: ButtonPresentationProps;
    linkWrapperHorizontal?: InjectableCss;
    linkWrapperVertical?: InjectableCss;
}

export const headerLinkListStyles: HeaderLinkListStyles = {
    // Sas --->
    headerLinkListWrapper: {},
    link: {
        typographyProps: {
            css: css`
                ${linkStyles("primary.contrast", "primary.contrast", false, true)}
                display: inline-block;
                font-weight: ${x => x.theme.design.typography.weight.medium};
                ${contentSpacingStyles(0, pxToRem(35), 0, 0)}

                &&& {
                    color: ${getColor("primary.contrast")};
                }
            `,
        },
    },
    linkWrapper: {
        css: css`
            display: flex;
        `,
    },
    button: {
        variant: "tertiary",
        shape: "rectangle",
        css: css`
            ${x => x.theme.button.tertiary.css}
            color: ${getColor("primary.contrast")};
            padding: ${pxToRem(5, 8)};

            &:hover,
            :focus,
            :active {
                background-color: ${getColor("primary")} !important;
                border-color: ${getColor("primary")} !important;
            }
        `,
    },
    linkWrapperHorizontal: {
        css: css`
            ${contentSpacingStyles(0, pxToRem(35), 0, 0, "padding")}
            ${({ theme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            ${contentSpacingStyles(0, pxToRem(8), 0, pxToRem(8), "padding")}
                        `,
                    ],
                    mediaOptions.max,
                )}
        `,
    },
    linkWrapperVertical: {
        css: css`
            padding: ${pxToRem(1)} 0 0 0;
        `,
    },
    // <--- Sas
};

/* Starter Custom Code - Start */
const defaultCustomCss = `& {
}

.list-link {
}
`;
/* Starter Custom Code - End */

export const HeaderLinkList: React.FC<OwnProps> = ({ fields, extendedStyles }) => {
    const links = useGetLinks(fields.links, o => o.fields.destination);

    const styles = useMergeStyles("headerLinkList", headerLinkListStyles, extendedStyles);

    // Sas -->
    const isScrollTop = useIsScrollTop();
    const history = useHistory();
    const { isInShell } = useShellContext();
    // <-- Sas

    if (links.length < 1) {
        return null;
    }

    /* Starter Custom Code - Start */
    const customCssWrapper = css`
        ${styles.headerLinkListWrapper?.css}
        ${fields.customCSS}
    `;

    // Sas -->
    const onClick = (url?: string, openInNewWindow?: boolean) => {
        if (!url) {
            return;
        }

        if (openInNewWindow) {
            window.open(url, "_blank");
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }
            window.location.href = url;
        } else {
            history.push(url);
        }
    };
    // <-- Sas

    const buttonStyles: ButtonPresentationProps = {
        ...styles.button,
        css: css`
            ${styles.button.css}
            ${!isScrollTop &&
            css`
                background-color: ${getColor("primary")};

                &:hover,
                :focus,
                :active {
                    background-color: transparent !important;
                    border-color: ${getColor("primary")} !important;
                }
            `}
        `,
    };

    // Sas --->
    const alignmentStyles = css`
        ${styles.headerLinkListWrapper.css}
        ${fields.customCSS}
        ${widgetWrapperStyles.css}
        ${fields.direction === "vertical"
            ? css`
                  flex-direction: column;
              `
            : css`
                  flex-direction: row;
              `}
    `;

    const directionStyles = css`
        ${fields.direction === "horizontal" ? styles.linkWrapperHorizontal?.css : styles.linkWrapperVertical?.css};
        ${styles.linkWrapper?.css}
        justify-content: ${fields.alignment};
    `;
    // <--- Sas

    return (
        <StyledWrapper css={alignmentStyles /* Sas */}>
            {links.map((link, index) => {
                return link.url ? (
                    // Sas - Styled Wrapper
                    <StyledWrapper css={directionStyles} key={`${index}${link.title}`}>
                        {/* Sas -- isButton & Button added */}
                        {(fields.links[index].fields as any).isButton ? (
                            <>
                                {(fields.links[index].fields as any).buttonVariant !== "headerType" ? (
                                    <Button
                                        // eslint-disable-next-line react/no-array-index-key
                                        key={`${link.title}-${index}Headerbutton`}
                                        onClick={() => onClick(link.url, fields.links[index].fields.openInNewWindow)}
                                        variant={(fields.links[index].fields as any).buttonVariant}
                                    >
                                        {fields.links[index].fields.overriddenTitle || link.title}
                                    </Button>
                                ) : (
                                    <Button
                                        // eslint-disable-next-line react/no-array-index-key
                                        key={`${link.title}-${index}button`}
                                        {...buttonStyles}
                                        onClick={() => onClick(link.url, fields.links[index].fields.openInNewWindow)}
                                    >
                                        {fields.links[index].fields.overriddenTitle || link.title}
                                    </Button>
                                )}
                            </>
                        ) : (
                            // Sas --->
                            <Link
                                // eslint-disable-next-line react/no-array-index-key
                                key={`${link.title}-${index}link`}
                                href={link.url}
                                target={fields.links[index].fields.openInNewWindow ? "_blank" : ""}
                                {...styles.link}
                            >
                                {fields.links[index].fields.overriddenTitle || link.title /* Sas */}
                            </Link>
                            // <--- Sas
                        )}
                        {/* Sas -- isButton added */}
                    </StyledWrapper>
                ) : // Sas - Styled Wrapper
                null;
            })}
        </StyledWrapper>
    );
    /* Starter Custom Code - End */
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: HeaderLinkList,
    definition: {
        group: "Header",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const {
                        destination: { type, value },
                        overriddenTitle,
                    } = item.fields;
                    if (overriddenTitle) {
                        return overriddenTitle;
                    }
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        let displayValue = "";
                        const categories = (state as any).pageEditor.categories;
                        if (!categories) {
                            displayValue = "Loading...";
                        } else {
                            const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                            if (matchingCategories.length === 0) {
                                displayValue = "Unknown";
                            } else {
                                displayValue = matchingCategories[0].shortDescription;
                            }
                        }

                        return displayValue;
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "Translatable", // Sas
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    /* Starter Custom Code - Start */
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "icon",
                        editorTemplate: "DropDownField",
                        options: Object.entries(headerLinkListIcons).map(([value, { displayName }]) => ({
                            displayName,
                            value,
                        })),
                        defaultValue: "",
                    },
                    {
                        name: "iconPosition",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Left", value: "left" },
                            { displayName: "Right", value: "right" },
                        ],
                        defaultValue: "left",
                        isRequired: true,
                        isVisible: x => x.fields.icon,
                    },
                    /* Starter Custom Code - End */
                    // Store Custom Code
                    {
                        name: "isButton",
                        displayName: "Style as Button",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        variant: "toggle",
                    },
                    {
                        name: "buttonVariant",
                        displayName: "Button Variant",
                        editorTemplate: "DropDownField",
                        defaultValue: "headerType",
                        options: [
                            {
                                displayName: "Primary",
                                value: "primary",
                            },
                            {
                                displayName: "Secondary",
                                value: "secondary",
                            },
                            {
                                displayName: "Tertiary",
                                value: "tertiary",
                            },
                            {
                                displayName: "Header Type",
                                value: "headerType",
                            },
                        ],
                        isVisible: widget => widget.fields.isButton,
                    },
                    // Sas
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
            // Sas --->
            {
                name: fields.direction,
                editorTemplate: "DropDownField",
                defaultValue: "vertical",
                fieldType: "General",
                options: [
                    {
                        displayName: "Vertical",
                        value: "vertical",
                    },
                    {
                        displayName: "Horizontal",
                        value: "horizontal",
                    },
                ],
                hideEmptyOption: true,
            },
            {
                name: fields.alignment,
                editorTemplate: "DropDownField",
                defaultValue: "left",
                fieldType: "General",
                options: [
                    {
                        displayName: "Left",
                        value: "left",
                    },
                    {
                        displayName: "Center",
                        value: "center",
                    },
                    {
                        displayName: "Right",
                        value: "right",
                    },
                ],
                hideEmptyOption: true,
            },
            // <--- Sas
        ],
    },
};

export default widgetModule;
